/**
 * Production console output is captured by PostHog session replay, and
 * logger.warn is forwarded as a PostHog client_warning event. tRPC log
 * payloads must therefore shrink to timing plus a bounded error surface
 * before they leave the app — never the operation input or result.
 */

const MAX_ERROR_MESSAGE_LENGTH = 256;

interface TrpcErrorShape {
	name?: string;
	message?: string;
	data?: { code?: string; httpStatus?: number } | null;
}

/**
 * An error delivered inside a result envelope instead of a thrown
 * TRPCClientError — loggerLink's own error detection covers both shapes.
 */
function envelopeError(result: unknown): TrpcErrorShape | undefined {
	if (!result || typeof result !== "object" || result instanceof Error) {
		return undefined;
	}
	const inner = (result as { result?: unknown }).result;
	if (!inner || typeof inner !== "object") {
		return undefined;
	}
	const error = (inner as { error?: unknown }).error;
	if (!error || typeof error !== "object") {
		return undefined;
	}
	return error as TrpcErrorShape;
}

export function isTrpcErrorResult(result: unknown): boolean {
	return result instanceof Error || envelopeError(result) !== undefined;
}

/**
 * `message` is free text chosen by whoever threw the server-side TRPCError,
 * and several server messages interpolate user-supplied values. Keep it only
 * for transport-level failures — errors that carry no server `data` shape
 * ("Failed to fetch", aborts), whose text the browser or tRPC client wrote.
 */
function summarizeError(shape: TrpcErrorShape, serverShaped: boolean) {
	const message = serverShaped
		? undefined
		: shape.message?.slice(0, MAX_ERROR_MESSAGE_LENGTH);
	return {
		name: shape.name,
		message,
		code: shape.data?.code,
		httpStatus: shape.data?.httpStatus,
	};
}

export function redactLoggerArgForProduction(arg: unknown): unknown {
	if (typeof arg !== "object" || arg === null) {
		return arg;
	}
	const { elapsedMs, result } = arg as { elapsedMs?: number; result?: unknown };
	if (result instanceof Error) {
		const { data } = result as { data?: TrpcErrorShape["data"] };
		return {
			elapsedMs,
			error: summarizeError(
				{ name: result.name, message: result.message, data },
				data != null,
			),
		};
	}
	const shape = envelopeError(result);
	if (shape) {
		// Envelope errors are server-shaped by definition.
		return { elapsedMs, error: summarizeError(shape, true) };
	}
	return { elapsedMs };
}

interface TrpcLogSink {
	info: (...args: unknown[]) => void;
	warn: (...args: unknown[]) => void;
}

/**
 * Builds loggerLink's console. Redaction lives inside the console methods —
 * not only behind the link's `enabled` gate — so relaxing that gate can
 * never silently restore the payload leak.
 */
export function makeTrpcLoggerConsole(isDev: boolean, sink: TrpcLogSink) {
	const redact = (args: unknown[]) =>
		isDev ? args : args.map(redactLoggerArgForProduction);
	return {
		log(...args: unknown[]) {
			sink.info("TRPC Client Log:", ...redact(args));
		},
		error(...args: unknown[]) {
			sink.warn("TRPC Client Error:", ...redact(args));
		},
	};
}
